import { type ReactNode } from 'react';

export interface SheetSectionProps {
  /** Mono uppercase section label (e.g. "Type", "Properties", "Status"). */
  label: ReactNode;
  /** Optional trailing control rendered after the hairline rule. */
  action?: ReactNode;
  /** Section body. */
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

/**
 * Labeled section group for the right-anchored Sheet bodies (Details / History
 * / Project settings). A mono uppercase label, a hairline rule that fills the
 * remaining width, and an optional trailing action — then the section content.
 * CSS namespace: `pn-sheet-section`.
 */
export function SheetSection({
  label,
  action,
  children,
  className,
  'data-testid': dataTestId
}: SheetSectionProps) {
  const cls = ['pn-sheet-section', className].filter(Boolean).join(' ');
  return (
    <section className={cls} data-testid={dataTestId}>
      <div className="pn-sheet-section-head">
        <span className="pn-sheet-section-label">{label}</span>
        <span className="pn-sheet-section-rule" aria-hidden="true" />
        {action}
      </div>
      {children}
    </section>
  );
}

export interface SheetFieldProps {
  /** Mono uppercase field label, shown above the control. */
  label: ReactNode;
  /** Optional id of the control the label describes. */
  htmlFor?: string;
  /** The control (a picker / input / pill row). */
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

/**
 * A label→control field pairing inside a SheetSection: a mono label stacked
 * above its control. CSS namespace: `pn-sheet-field`.
 */
export function SheetField({
  label,
  htmlFor,
  children,
  className,
  'data-testid': dataTestId
}: SheetFieldProps) {
  const cls = ['pn-sheet-field', className].filter(Boolean).join(' ');
  return (
    <div className={cls} data-testid={dataTestId}>
      <label className="pn-sheet-field-label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
    </div>
  );
}
